import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/solid";
import axios from "axios";
import { useEffect, useState } from "react";
import {
  FaCogs,
  FaEnvelope,
  FaEnvelopeOpenText,
  FaFolderOpen,
  FaHeadSideVirus,
  FaSpinner,
  FaSync,
} from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../api";
import { IMessage } from "../api/services/messaging.service";
import { IUserResponse } from "../api/services/user.service";
import { BACKEND_BASE_URL } from "../config";
import { usePagination } from "../hooks/usePagination";
import Pagination from "./shared/Pagination";

export default function MessageSection({ userId }: { userId?: string }) {
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const { page, paginationProps, searchTerm, updatePagination, setPage } =
    usePagination();

  useEffect(() => {
    if (userId) {
      getMessagesOfUsers(userId);
    }
  }, [userId]);

  async function getMessagesOfUsers(userId: string) {
    try {
      setLoading(true);
      const { data } = await api.messaging.getMessagesOfUser(userId);

      if (data.result) {
        const pageContent = data.result;
        setMessages(pageContent);
        // updatePagination(data);
        setLoading(false);
      }
    } catch (e) {
      if (axios.isAxiosError(e)) {
        if (e.response?.data) {
          toast.error(e.response?.data.errorMessage);
        }
      } else {
        console.log(e);
        toast.error(
          "Something really went wrong, you might want to contact support!"
        );
      }
    }
  }

  function MessageRow({ message }: { message: IMessage }) {
    const [open, setOpen] = useState(false);
    const [user, setUser] = useState<IUserResponse | null>(null);

    async function openMessageInNewTab(messageId: string) {
      if (BACKEND_BASE_URL.indexOf("dev") > -1) {
        window.open(
          `https://admin.dev.ktr.studio/message/${messageId}/render`,
          "_blank"
        );
      } else {
        window.open(
          `https://admin.ktr.studio/message/${messageId}/render`,
          "_blank"
        );
      }
    }

    return (
      <div className="break-all py-4 px-4">
        <div className="flex items-center" onClick={() => setOpen(!open)}>
          <div className="min-w-0 flex-1 flex items-start">
            <div className="min-w-0 flex-1 md:grid md:grid-cols-2 md:gap-4">
              <div className="flex items-center">
                <div className="flex-shrink-0 mr-3">
                  <FaEnvelope className="h-10 w-10 text-slate-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-red-600 break-all">
                    {message.subject}
                  </p>
                  <p className="mt-2 flex items-center text-sm text-gray-500">
                    {message.event}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="mr-2">
            {!open && (
              <ChevronDownIcon
                className="h-8 w-8 text-gray-400"
                aria-hidden="true"
              />
            )}
            {open && (
              <ChevronUpIcon
                className="h-8 w-8 text-gray-400"
                aria-hidden="true"
              />
            )}
          </div>
        </div>
        <div
          className={`flex flex-col md:flex-row items-start space-y-6 md:space-y-0 py-4 md:space-x-4 ${
            open ? "" : "hidden"
          }`}
        >
          <div className="md:w-1/2">
            <div className="flex font-semibold text-red-600 space-x-1 items-center mb-4">
              <FaCogs />
              <div>Actions</div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center">
                <button
                  // onClick={() => handleMessageClone(message)}
                  type="button"
                  className="bg-red-700 p-2 text-red-400 rounded-xl hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <FaSync className="h-6 w-6" aria-hidden="true" />
                </button>
                <div className="ml-2 text-slate-700 font-medium">
                  Re-send message
                </div>
              </div>
              <div className="flex items-center">
                <button
                  onClick={() => openMessageInNewTab(message.id)}
                  type="button"
                  className="bg-red-700 p-2 text-red-400 rounded-xl hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <FaFolderOpen className="h-6 w-6" aria-hidden="true" />
                </button>
                <div className="ml-2 text-slate-700 font-medium">
                  Render Mail
                </div>
              </div>
            </div>
          </div>
          <div className="md:w-1/2">
            <div className="flex font-semibold text-red-600 space-x-1 items-center mb-4">
              <FaHeadSideVirus />
              <div>Headers</div>
            </div>
            <div className="md:hidden text-sm text-gray-900 font-mono">
              {JSON.stringify(message.metaData, null, 2)}
            </div>
            <pre className="hidden md:block text-sm font-mono bg-slate-200 text-slate-700 p-4 rounded-lg">
              {JSON.stringify(message.metaData, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col rounded-2xl shadow-xl bg-white grow ">
      <div className="flex items-center p-4 w-full border-b justify-between">
        <div className="flex items-center">
          <div className="flex items-center mr-10 rounded-lg">
            <div className="">
              <FaEnvelopeOpenText size={20} className="text-red-700" />
            </div>
            <span className="ml-2 font-medium leading-6 text-slate-700 text-lg flex items-center">
              Messages
            </span>
          </div>
        </div>
        <div className=""></div>
      </div>
      {loading && (
        <div className="flex items-center justify-center p-20 text-slate-500">
          <FaSpinner size={30} className="animate-spin" />
        </div>
      )}
      {!loading && messages.length > 0 && (
        <div className="flex flex-col divide-y divide-gray-200 w-full overflow-scroll overflow-x-hidden">
          {messages.map((message, i) => (
            <MessageRow key={i} message={message} />
          ))}
        </div>
      )}
      {!loading && messages.length === 0 && (
        <div className="flex flex-col items-center justify-center p-8 text-slate-500">
          <div className="font-semibold text-slate-700 mb-2">
            No Messages Found
          </div>
        </div>
      )}
      <Pagination {...paginationProps} goToPage={setPage} />
    </div>
  );
}
